import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { useFormik } from "formik";
import * as yup from "yup";
import cr from "../../lib/request";
import RequiredForm from "../RequiredForm";
import { iconSize } from "../../const/sizes";
import useScreenWidth from "../../lib/useGetScreenWidth";
import PasswordInput from "../PasswordInput";
import FileUploader from "../FileUploader";
import adminRoles from "../../const/adminRoles";
import useTrigger from "../../globalState/useTrigger";

export default function TambahAdminAccount() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sw = useScreenWidth();

  const [fotoProfilPreview, setFotoProfilPreview] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const { trigger, setTrigger } = useTrigger();

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      name: "",
      username: "",
      password: "",
      adminAccount: false,
      adminBlog: false,
      adminCmsLandingpage: false,
      adminCourse: false,
      email: "",
      phone: "",
      image: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      name: yup.string().required("Nama harus diisi"),
      username: yup.string().required("Username harus diisi"),
      password: yup.string().required("Password Blog harus diisi"),
      image: yup
        .array()
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC yang diperbolehkan",
          (value) =>
            value &&
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/HEIC"
            )
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const url = `api/register`;
      // payload
      const payload = new FormData();
      payload.append("name", values.name);
      payload.append("username", values.username);
      payload.append("password", values.password);
      payload.append("email", values.email);
      payload.append("phone", values.phone);
      payload.append("adminAccount", values.adminAccount.toString());
      payload.append(
        "adminCmsLandingpage",
        values.adminCmsLandingpage.toString()
      );
      payload.append("adminBlog", values.adminBlog.toString());
      payload.append("adminCourse", values.adminCourse.toString());
      // img
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }

      cr.post(url, payload)
        .then((r) => {
          if (r.status === 201) {
            onClose();
            toast({
              title: r.data.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            setTrigger(!trigger);
          }
        })
        .catch((e) => {
          console.log(e);
          toast({
            title: "Gagal membuat akun",
            description: e?.response?.data?.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const handleInputRole = (role: { name: string; key: string; icon: any }) => {
    let value;
    switch (role.key) {
      case "adminAccount":
        value = !formik.values.adminAccount;
        break;
      case "adminCmsLandingpage":
        value = !formik.values.adminCmsLandingpage;
        break;
      case "adminBlog":
        value = !formik.values.adminBlog;
        break;
      case "adminCourse":
        value = !formik.values.adminCourse;
        break;
    }

    formik.setFieldValue(`${role.key}`, value);
  };

  return (
    <>
      {sw >= 500 ? (
        <Button
          colorScheme="p"
          className="clicky"
          leftIcon={<Icon as={Plus} fontSize={iconSize} weight="bold" />}
          pl={3}
          onClick={onOpen}
          flexShrink={0}
        >
          Tambah
        </Button>
      ) : (
        <IconButton
          colorScheme="p"
          className="clicky"
          aria-label="tambahKategoriBlog"
          icon={<Icon as={Plus} fontSize={iconSize} />}
          onClick={onOpen}
        />
      )}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        size={"lg"}
      >
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Buat Akun Admin</ModalHeader>

          <ModalBody>
            <form id="addAdminAccountForm" onSubmit={formik.handleSubmit}>
              <FormControl mb={4} isInvalid={formik.errors.name ? true : false}>
                <FormLabel>
                  Nama Lengkap
                  <RequiredForm />
                </FormLabel>
                <Input
                  name="name"
                  placeholder="Masukkan nama lengkap"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                />
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.username ? true : false}
              >
                <FormLabel>
                  Username
                  <RequiredForm />
                </FormLabel>
                <Input
                  name="username"
                  placeholder="Masukkan username"
                  onChange={formik.handleChange}
                  value={formik.values.username}
                />
                <FormHelperText opacity={0.5}>
                  Username digunakan untuk sign in akun ini
                </FormHelperText>
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.password ? true : false}
              >
                <FormLabel>
                  Password
                  <RequiredForm />
                </FormLabel>
                <PasswordInput
                  name="password"
                  placeholder="Masukkan password"
                  formik={formik}
                />
                <FormHelperText opacity={0.5}>
                  Password digunakan untuk sign in akun ini
                </FormHelperText>
                <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Role</FormLabel>
                <SimpleGrid columns={[2]} gap={2}>
                  {adminRoles.map((role, i) => (
                    <HStack
                      key={i}
                      justify={"center"}
                      borderRadius={8}
                      p={2}
                      // @ts-ignore
                      bg={formik.values[role.key] ? "p.500" : "var(--divider)"}
                      color={
                        // @ts-ignore
                        formik.values[role.key] ? "white" : "current"
                      }
                      cursor={"pointer"}
                      _hover={{
                        // @ts-ignore
                        bg: formik.values[role.key]
                          ? "p.500"
                          : "var(--divider2)",
                      }}
                      transition={"200ms"}
                      onClick={() => {
                        handleInputRole(role);
                      }}
                    >
                      <Icon
                        as={role.icon}
                        weight="fill"
                        fontSize={20}
                        ml={-1}
                      />
                      <Text>{role.name}</Text>
                    </HStack>
                  ))}
                </SimpleGrid>
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  placeholder="Masukkan email"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Phone</FormLabel>
                <Input
                  name="phone"
                  placeholder="Masukkan no telepon"
                  onChange={formik.handleChange}
                  value={formik.values.phone}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Foto Profil</FormLabel>
                <FileUploader
                  name="image"
                  formik={formik}
                  setPreviewUrl={setFotoProfilPreview}
                  accept=".jpg, .jpeg, .png, .HEIC, .svg"
                />
                {fotoProfilPreview ? (
                  <Image
                    borderRadius={8}
                    w={"100%"}
                    src={fotoProfilPreview}
                    mt={4}
                    mx={"auto"}
                  />
                ) : (
                  <Text fontSize={"small"} opacity={0.5} mt={1}>
                    Preview akan terlihat dibawah setelah mengunggah
                  </Text>
                )}
              </FormControl>
            </form>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button className="btn" onClick={onClose}>
              Batal
            </Button>

            <Button
              type="submit"
              form="addAdminAccountForm"
              colorScheme="p"
              className="clicky"
              isLoading={loading}
            >
              Simpan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
